import {Box, Flex, Text} from '@mantine/core';
import React from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {PreviewChart} from 'pipelines/network/PipelineDAO';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {getInputStepDataShape, getOutputStepDataShape} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import SingleStepDataPreview, {RawDataItem} from './SingleStepDataPreview';

export enum StepDataRole {
  INPUT,
  OUTPUT,
}

/** Persisted raw datas for a single step data, split by kind. */
export interface PersistedStepDataRawDatas {
  charts: Array<PipelineStepRawData<PipelineStepDataKind.CHART>>;
  dataFrames: Array<PipelineStepRawData<PipelineStepDataKind.DATASET>>;
  files: Array<PipelineStepRawData<PipelineStepDataKind.FILE>>;
  plates: Array<PipelineStepRawData<PipelineStepDataKind.PLATE>>;
}

/** Live/manual preview raw datas for a single step data, split by kind. Plates are not previewable. */
export interface PreviewStepDataRawDatas {
  charts: Array<PreviewChart>;
  dataFrames: Array<DataFrame>;
  files: Array<ScratchFileContent>;
}

/** Persisted raw datas keyed by step data name. */
export interface PersistedStepDatas {
  [stepDataName: string]: PersistedStepDataRawDatas;
}

/** Preview raw datas keyed by step data name. */
export interface PreviewStepDatas {
  [stepDataName: string]: PreviewStepDataRawDatas;
}

export interface PresentationalStepDataPreviewProps {
  stepType: PipelineStepType;
  stepVersion: number;
  stepDataRole: StepDataRole;
  clientStateId: string;
  /** Persisted step data. Exactly one of `persisted` / `preview` should be provided. */
  persisted?: PersistedStepDatas;
  /** Live/manual preview step data. Exactly one of `persisted` / `preview` should be provided. */
  preview?: PreviewStepDatas;
}

interface StepDataGroupData {
  stepDataName: string;
  displayName: string;
  items: Array<RawDataItem>;
}

function getStepDataDisplayName(
  stepType: PipelineStepType,
  stepVersion: number,
  stepDataRole: StepDataRole,
  stepDataName: string
): string {
  const shape =
    stepDataRole === StepDataRole.INPUT
      ? getInputStepDataShape(stepType, stepVersion, stepDataName)
      : getOutputStepDataShape(stepType, stepVersion, stepDataName);
  return shape.displayName;
}

function persistedRawDataItems(rawDatas: PersistedStepDataRawDatas): Array<RawDataItem> {
  return [
    ...rawDatas.charts.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.CHART, preview: false, rawData})
    ),
    ...rawDatas.dataFrames.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.DATASET, preview: false, rawData})
    ),
    ...rawDatas.files.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.FILE, preview: false, rawData})
    ),
    ...rawDatas.plates.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.PLATE, preview: false, rawData})
    ),
  ];
}

function previewRawDataItems(rawDatas: PreviewStepDataRawDatas): Array<RawDataItem> {
  return [
    ...rawDatas.charts.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.CHART, preview: true, rawData})
    ),
    ...rawDatas.dataFrames.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.DATASET, preview: true, rawData})
    ),
    ...rawDatas.files.map(
      (rawData): RawDataItem => ({kind: PipelineStepDataKind.FILE, preview: true, rawData})
    ),
  ];
}

interface StepDataGroupProps {
  /** User-facing name of the step data. */
  stepDataDisplayName: string;
  /** Step data items to preview. */
  items: Array<RawDataItem>;
  /** Whether to show `stepDataDisplayName` in a header. */
  showHeader: boolean;
  /**
   * Whether the preview item in should take the full available height of the sidebar.
   *
   * This should be `true` if there is exactly 1 item in the sidebar, across all step datas.
   */
  fullHeight: boolean;
  clientStateId: string;
}

/**
 * Render one step data's items in order, optionally preceded by the step data's display name header.
 */
function StepDataGroup({
  stepDataDisplayName,
  items,
  showHeader,
  fullHeight,
  clientStateId,
}: StepDataGroupProps): JSX.Element {
  // Persisted raw datas have a stable id; preview raw datas do not, so fall back to name + index.
  const getItemKey = (item: RawDataItem, index: number): string => {
    const {rawData} = item;
    if ('id' in rawData && rawData.id != null) {
      return rawData.id;
    }
    const name = 'name' in rawData ? rawData.name : '';
    return `${name}-${index}`;
  };

  return (
    <>
      {showHeader && (
        <Text fw={700} size="lg" mt="sm" mb="sm" mx="md">
          {stepDataDisplayName}
        </Text>
      )}
      {items.map((item, index) => (
        <SingleStepDataPreview
          key={getItemKey(item, index)}
          item={item}
          clientStateId={`${clientStateId}-${getItemKey(item, index)}`}
          fullHeight={fullHeight}
        />
      ))}
    </>
  );
}

/**
 * Presentational list of a step's charts, data frames, files, and plates, grouped by step data.
 *
 * Renders either persisted step datas or live/ephemeral/in-memory preview step data, depending on which is
 * provided.
 *
 * @package
 */
export default function PresentationalStepDataPreview({
  stepType,
  stepVersion,
  stepDataRole,
  clientStateId,
  persisted,
  preview,
}: PresentationalStepDataPreviewProps): JSX.Element {
  const isPreview = preview != null;

  const groups: Array<StepDataGroupData> = [];
  Object.entries(preview ?? persisted ?? {}).forEach(([stepDataName, rawDatas]) => {
    const items = isPreview
      ? previewRawDataItems(rawDatas as PreviewStepDataRawDatas)
      : persistedRawDataItems(rawDatas as PersistedStepDataRawDatas);
    if (items.length > 0) {
      groups.push({
        stepDataName,
        displayName: getStepDataDisplayName(stepType, stepVersion, stepDataRole, stepDataName),
        items,
      });
    }
  });

  if (groups.length === 0) {
    return (
      <Box mx="md" my="sm">
        {isPreview ? (
          <InlineBanner bannerType={InlineBannerType.WARNING} text="No preview data available." />
        ) : (
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text={
              stepDataRole === StepDataRole.INPUT ? 'This step has no inputs.' : 'This step has no outputs.'
            }
          />
        )}
      </Box>
    );
  }

  const showHeaders = groups.length > 1;
  // A lone item fills the sidebar height instead of a fixed height, and the sidebar does not scroll.
  const isSingleItem = groups.length === 1 && groups[0].items.length === 1;
  return (
    <Flex direction="column" h="100%" py="sm" sx={{overflowY: isSingleItem ? 'hidden' : 'auto'}}>
      {groups.map((group) => (
        <StepDataGroup
          key={group.stepDataName}
          stepDataDisplayName={group.displayName}
          items={group.items}
          clientStateId={clientStateId}
          showHeader={showHeaders}
          fullHeight={isSingleItem}
        />
      ))}
    </Flex>
  );
}
